import { useState, useRef, useEffect } from 'react';
import { NavLink, useNavigate, useLocation } from 'react-router-dom';
import { useSelector, useDispatch } from 'react-redux'; 
import { 
  Bell, 
  Wallet, 
  List,
  Users,
  ChevronDown, 
  MessageSquare,
  Menu,
  Clock,
  PhoneCall,
  BookOpen,
  Home,
  Radio,
  Sparkles,
  Scale,
  Stethoscope
} from 'lucide-react';

import { useUserLogout } from '../../hooks/userAuthHooks/useUserLogout'; 
import UserDropdown from './UserDropdown';
import UserPhoneSidebar from './UserPhoneSidebar'; 
import logo from '../../assets/trueAdvisoLogo/trueAdvisologofull.jpeg';

import type { RootState } from '../../store/store'; 
import { setMinimized } from '../../store/slices/queueSlice'; 

// 🟢 IMPORT YOUR CUSTOM HOOK
import { useUserDashboard } from '../../hooks/userDashboardHooks/useUserDashboard'; 

export interface UserProfile {
  name?: string;
  userCode?: string;
  profilePhotoUrl?: string;
  isPremium?: boolean; 
}

interface UserNavbarProps {
  profile?: UserProfile | null;
  walletBalance?: number; 
}

export default function UserNavbar({ profile }: UserNavbarProps) {
  const { logout, isLoggingOut } = useUserLogout();
  const dispatch = useDispatch();
  const navigate = useNavigate(); 
  const location = useLocation();
  
  // 🟢 CALL THE HOOK TO FETCH LIVE DASHBOARD DATA
  const { stats, isLoading } = useUserDashboard();
  
  // 🟢 EXTRACT WALLET BALANCE
  const liveWalletBalance = stats?.walletBalance || 0;

  const [isDropdownOpen, setIsDropdownOpen] = useState<boolean>(false);
  const [isSidebarOpen, setIsSidebarOpen] = useState<boolean>(false); 
  
  const dropdownRef = useRef<HTMLDivElement>(null);

  const rawQueueState = useSelector((state: RootState) => state.queue);
  const activeSession = useSelector((state: RootState | any) => state.activeSession?.session);

  const activeQueue = rawQueueState?.activeQueue;
  const isMinimized = rawQueueState?.isMinimized;

  const photoUrl = profile?.profilePhotoUrl || localStorage.getItem('userProfileImage');
  const displayName = profile?.name || localStorage.getItem('userName') || 'User';
  const firstName = displayName.split(' ')[0];

  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsDropdownOpen(false);
      }
    }
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  useEffect(() => {
    if (isSidebarOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'unset';
    }
    return () => { document.body.style.overflow = 'unset'; };
  }, [isSidebarOpen]);

  // 🟢 EXPERT CATEGORIES FOR HOVER DROPDOWN
  const expertCategories = [
    { label: 'Astrology', to: '/experts/astrology', icon: Sparkles },
    { label: 'Lawyer', to: '/experts/lawyer', icon: Scale },
    { label: 'Doctor', to: '/experts/doctor', icon: Stethoscope },
  ];

  // Check if current route is anywhere under /experts
  const isExpertsActive = location.pathname.startsWith('/experts');

  // Standard NavLink styling
  const navClass = ({ isActive }: { isActive: boolean }) =>
    `relative flex items-center gap-2 px-4 py-2 text-sm font-medium transition-all duration-300 ${
      isActive
        ? 'text-blue-600 bg-blue-50 rounded-lg'
        : 'text-gray-600 hover:text-blue-700 hover:bg-blue-50 rounded-lg'
    }`;

  return (
    <>
      <header className="sticky top-0 w-full z-50 bg-white/95 backdrop-blur-md shadow-sm border-b border-gray-200">
        <div className="flex items-center justify-between px-4 sm:px-6 h-16 w-full">
          
          <div className="flex items-center gap-3">
            <button 
              className="p-2 -ml-2 text-gray-600 hover:bg-gray-100 rounded-lg lg:hidden transition-colors"
              onClick={() => setIsSidebarOpen(true)}
            >
              <Menu className="w-6 h-6" />
            </button>
            
            <NavLink to="/user/dashboard" className="flex items-center">
              <img 
                src={logo} 
                alt="TrueAdviso" 
                className="h-10 sm:h-16 w-auto object-contain cursor-pointer" 
              />
            </NavLink>
          </div>

          {/* 🟢 DESKTOP NAVIGATION MENU */}
          <nav className="hidden lg:flex items-center gap-1.5 absolute left-1/2 -translate-x-1/2">
            
            {/* 1. HOME */}
            <NavLink to="/" className={navClass}>
              <Home className="w-4 h-4" />
              Home
            </NavLink>

            {/* 2. EXPERTS (NON-CLICKABLE PARENT WITH HOVER DROPDOWN) */}
            <div className="relative group py-2">
              <button 
                type="button"
                className={`flex items-center gap-1.5 px-4 py-2 text-sm font-medium rounded-lg transition-all duration-300 cursor-default ${
                  isExpertsActive 
                    ? 'text-blue-600 bg-blue-50' 
                    : 'text-gray-600 hover:text-blue-700 hover:bg-blue-50'
                }`}
              >
                <Users className="w-4 h-4" />
                <span>Experts</span>
                <ChevronDown className="w-3.5 h-3.5 text-gray-400 group-hover:rotate-180 transition-transform duration-200" />
              </button>

              {/* Dropdown Menu */}
              <div className="absolute left-0 top-full pt-1 w-48 opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all duration-200 z-50">
                <div className="bg-white rounded-xl shadow-xl border border-gray-100 py-2 overflow-hidden">
                  {expertCategories.map((subItem) => (
                    <NavLink
                      key={subItem.label}
                      to={subItem.to}
                      className={({ isActive }) =>
                        `flex items-center gap-2.5 px-4 py-2.5 text-sm font-medium transition-colors ${
                          isActive
                            ? 'text-blue-600 bg-blue-50/80 font-semibold'
                            : 'text-gray-700 hover:text-blue-600 hover:bg-gray-50'
                        }`
                      }
                    >
                      <subItem.icon className="w-4 h-4 text-gray-400 group-hover:text-blue-600 transition-colors" />
                      {subItem.label}
                    </NavLink>
                  ))}
                </div>
              </div>
            </div>

            {/* 3. CATEGORIES */}
            <NavLink to="/categories" className={navClass}>
              <List className="w-4 h-4" />
              Categories
            </NavLink>

            {/* 4. LIVE SESSIONS */}
            <NavLink to="/live-sessions" className={navClass}>
              <Radio className="w-4 h-4" />
              Live Sessions
            </NavLink>

            {/* 5. CHATS */}
            <NavLink to="/user/messages" className={navClass}>
              <MessageSquare className="w-4 h-4" />
              Chats
            </NavLink>

            {/* 6. BLOGS */}
            <NavLink to="/blogs" className={navClass}>
              <BookOpen className="w-4 h-4" />
              Blogs
            </NavLink>

          </nav>

          <div className="flex items-center gap-2 sm:gap-4">
            
            {activeSession && (
              <button 
                onClick={() => navigate(`/${activeSession.type}/${activeSession.expertCode}`, { state: activeSession })}
                className="flex relative items-center gap-1.5 sm:gap-2 px-2 sm:px-4 py-1 sm:py-1.5 bg-emerald-500 hover:bg-emerald-600 text-white rounded-full transition-all duration-200 cursor-pointer shadow-md"
                title="Return to Active Session"
              >
                <span className="relative flex h-2 w-2 sm:h-2.5 sm:w-2.5 flex-shrink-0">
                  <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-white opacity-75"></span>
                  <span className="relative inline-flex rounded-full h-2 w-2 sm:h-2.5 sm:w-2.5 bg-white"></span>
                </span>
                
                <PhoneCall className="w-3 h-3 sm:w-3.5 sm:h-3.5 flex-shrink-0" />
                
                <span className="text-[10px] sm:text-xs font-bold whitespace-nowrap capitalize">
                  <span className="hidden sm:inline">Resume </span>{activeSession.type}
                </span>
              </button>
            )}

            {activeQueue && isMinimized && (
              <button 
                onClick={() => {
                  dispatch(setMinimized(false));
                }}
                className="relative flex items-center gap-2 px-4 py-1.5 bg-red-50 hover:bg-red-100 border border-red-200 text-red-600 rounded-full transition-all duration-200 cursor-pointer shadow-sm"
                title="Return to Queue"
              >
                <Clock className="w-4 h-4 animate-pulse text-red-500" />
                <span className="text-xs font-bold whitespace-nowrap">
                  Queue: #{activeQueue.queueRank}
                </span>
              </button>
            )}

            <button 
              className="flex items-center gap-1 sm:gap-1.5 px-2 sm:px-3 py-1 sm:py-1.5 text-gray-600 hover:text-blue-600 hover:bg-blue-50 rounded-full border border-gray-200 transition-colors" 
              title="Wallet Balance"
              onClick={() => navigate('/user/wallet')}
            >
              <Wallet className="w-4 h-4" />
              <span className="text-xs sm:text-sm font-bold">
                {isLoading ? '...' : `₹${liveWalletBalance.toFixed(2)}`}
              </span>
            </button>
            
            <button className="relative p-1.5 sm:p-2 text-gray-500 hover:text-blue-600 hover:bg-blue-50 rounded-full transition-colors" title="Notifications">
              <Bell className="w-5 h-5" />
              <span className="absolute top-1 right-1 sm:top-1.5 sm:right-1.5 w-2 h-2 bg-red-500 rounded-full border-2 border-white"></span>
            </button>

            <div className="relative" ref={dropdownRef}>
              <button 
                onClick={() => setIsDropdownOpen(!isDropdownOpen)}
                className="group flex items-center gap-1 sm:gap-2 ml-1 sm:ml-2 p-1 pr-1 sm:pr-2 border border-transparent hover:border-gray-200 hover:bg-gray-50 rounded-full transition-all duration-200"
              >
                {photoUrl ? (
                  <img 
                    src={photoUrl} 
                    alt="Profile" 
                    className="w-7 h-7 sm:w-8 sm:h-8 rounded-full object-cover border border-gray-200 transition-transform duration-200 group-hover:scale-105" 
                  />
                ) : (
                  <div className="w-7 h-7 sm:w-8 sm:h-8 rounded-full bg-gradient-to-tr from-cyan-500 to-blue-600 flex items-center justify-center text-white text-xs sm:text-sm font-bold transition-transform duration-200 group-hover:scale-105">
                    {firstName[0]}
                  </div>
                )}
                <ChevronDown className={`w-3 h-3 sm:w-4 sm:h-4 text-gray-500 transition-transform duration-200 ${isDropdownOpen ? 'rotate-180' : ''}`} />
              </button>

              {isDropdownOpen && (
                <UserDropdown 
                  profile={profile} 
                  logout={logout} 
                  isLoggingOut={isLoggingOut} 
                  closeDropdown={() => setIsDropdownOpen(false)} 
                />
              )}
            </div>
          </div>
        </div>
      </header>

      <UserPhoneSidebar 
        isOpen={isSidebarOpen} 
        onClose={() => setIsSidebarOpen(false)} 
        profile={profile}
        logout={logout}
        isLoggingOut={isLoggingOut}
      />
    </>
  );
}